import { useNavigate } from 'react-router-dom';
import { UnstyledButton, Burger } from '@mantine/core';
import { IconLayoutSidebarLeftCollapse, IconLayoutSidebarLeftExpand } from '@tabler/icons-react';
import { Title } from '@/components/ui/core';
import { Group, Box } from '@/components/ui/layout';
import { ActionIcon } from '@/components/ui';
import AppSwitcherMenu from './AppSwitcherMenu';
import ThemeToggleButton from './ThemeToggleButton';
import NotificationBell from './NotificationBell';
import DomainSearchButton from './DomainSearchButton';
import UserProfileMenu from './UserProfileMenu';
import { IdleStatusBadge } from '@/components/ccms-components/realtime';

interface MainHeaderProps {
  onSidebarToggle?: () => void;
  sidebarCollapsed?: boolean;
  onLockScreen?: () => void;
  showBurger?: boolean;
}

export default function MainHeader({
  onSidebarToggle,
  sidebarCollapsed,
  onLockScreen,
  showBurger = true,
}: MainHeaderProps) {
  const navigate = useNavigate();

  return (
    <Group justify="space-between" h="100%" px="md">
      {/* Left Section: Burger + Logo + CCMS */}
      <Group gap="md">
        {/* Mobile: Burger menu (animated hamburger) */}
        {showBurger && (
          <Burger
            opened={!sidebarCollapsed}
            onClick={onSidebarToggle}
            size="sm"
            hiddenFrom="sm"
            aria-label={sidebarCollapsed ? 'Open navigation' : 'Close navigation'}
          />
        )}

        {/* Desktop: Sidebar collapse/expand toggle */}
        {showBurger && (
          <ActionIcon
            variant="subtle"
            onClick={onSidebarToggle}
            size="xl"
            visibleFrom="sm"
            aria-label={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
            style={{ color: 'var(--mantine-color-text)' }}
          >
            {sidebarCollapsed ? (
              <IconLayoutSidebarLeftExpand size={24} stroke={2.5} />
            ) : (
              <IconLayoutSidebarLeftCollapse size={24} stroke={2.5} />
            )}
          </ActionIcon>
        )}

        <UnstyledButton onClick={() => navigate('/home')}>
          <Group gap="xs">
            {/* Logo */}
            <Box
              component="img"
              src="/logo_512.png"
              alt="CCMS Logo"
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
              }}
            />

            {/* CCMS Text */}
            <Title
              order={2}
              style={{
                color: 'var(--mantine-color-text)',
                fontWeight: 700,
              }}
            >
              CCMS
            </Title>
          </Group>
        </UnstyledButton>

        <AppSwitcherMenu />
      </Group>

      {/* Center Section: Domain Search */}
      <Box visibleFrom="md">
        <DomainSearchButton />
      </Box>

      {/* Right Section: Theme + Notifications + Idle Status + User Profile */}
      <Group gap="sm">
        <ThemeToggleButton />
        <NotificationBell />
        <IdleStatusBadge />
        <UserProfileMenu onLockScreen={onLockScreen} />
      </Group>
    </Group>
  );
}
